import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, Send, Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Explain this lesson simply",
  "Give me examples",
  "Create practice questions",
  "Summarize this module",
  "Help me prepare for the quiz",
  "Translate explanation to French",
];

export default function AITutor({ courseId, moduleId }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (open && courseId) {
      api.get(`/api/student/tutor/history/${courseId}`).then(({ data }) => {
        setMsgs(data || []);
      }).catch(() => {});
    }
  }, [open, courseId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async (message) => {
    const content = (message ?? text).trim();
    if (!content || sending) return;
    setText("");
    setMsgs((m) => [...m, { role: "user", content, id: `tmp-${Date.now()}` }]);
    setSending(true);
    try {
      const { data } = await api.post("/api/student/tutor/ask", {
        course_id: courseId, module_id: moduleId, message: content,
      });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply, id: data.id }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tutor unavailable");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="tutor-open"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 btn-secondary shadow-lg flex items-center gap-2"
      >
        <Sparkles className="w-4 h-4" /> Ask Tutor
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-end md:justify-end p-0 md:p-6 bg-black/30 backdrop-blur-sm">
          <div className="bg-white w-full md:w-[420px] h-[70vh] md:h-[640px] rounded-t-2xl md:rounded-2xl border border-[#E5E7EB] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#064E3B] text-white flex items-center justify-center">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-display font-bold text-sm">AI Tutor</div>
                  <div className="text-xs text-[#4B5563]">Asks based on this course</div>
                </div>
              </div>
              <button data-testid="tutor-close" onClick={() => setOpen(false)} className="p-2 hover:bg-[#F3F4F6] rounded-md">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {msgs.length === 0 && (
                <div className="text-center py-8">
                  <MessageCircle className="w-10 h-10 mx-auto text-[#064E3B]" />
                  <p className="text-sm text-[#4B5563] mt-2">Ask anything about this course.</p>
                  <div className="flex flex-wrap gap-2 justify-center mt-4">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid={`tutor-suggest-${s.slice(0,8)}`}
                        onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-[#E5E7EB] hover:border-[#D92D20] hover:text-[#D92D20]">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-xl text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#D92D20] text-white"
                      : "bg-[#F3F4F6] text-[#111827]"
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-[#F3F4F6] text-[#4B5563] px-3.5 py-2.5 rounded-xl text-sm">
                    Thinking…
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-[#E5E7EB] flex gap-2"
            >
              <input
                data-testid="tutor-input"
                className="flex-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]"
                placeholder="Ask anything…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={sending}
              />
              <button data-testid="tutor-send" type="submit" disabled={sending} className="btn-primary px-4">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
